import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Gear, CheckCircle, ChatCircleDots, ShieldCheck, Globe, Key, EnvelopeSimple, Phone, ListChecks } from "@phosphor-icons/react";
import { Video, Image as ImageIcon, Trash2, Plus, Upload, CheckCircle2, Headphones, Send } from "lucide-react";

export default function SystemSettings() {
  return (
    <div data-testid="system-settings-page">
      <PageHeader title="System Settings" subtitle="Platform defaults, landing page content, SEO, SSH, SMTP, security, and rules" />
      <Tabs defaultValue="general" className="mt-2">
        <TabsList className="flex h-auto flex-wrap gap-1" data-testid="settings-tabs">
          <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
          <TabsTrigger value="chat" data-testid="settings-tab-chat">Chat widget</TabsTrigger>
          <TabsTrigger value="vicibots-widget" data-testid="settings-tab-vicibots-widget">ViciBots Widget</TabsTrigger>
          <TabsTrigger value="videos" data-testid="settings-tab-videos">Intro videos</TabsTrigger>
          <TabsTrigger value="demo-calls" data-testid="settings-tab-demo-calls">AI Demo Calls</TabsTrigger>
          <TabsTrigger value="metadata" data-testid="settings-tab-metadata">Meta Data</TabsTrigger>
          <TabsTrigger value="smtp" data-testid="settings-tab-smtp">SMTP</TabsTrigger>
          <TabsTrigger value="ssh" data-testid="settings-tab-ssh">SSH</TabsTrigger>
          <TabsTrigger value="captcha" data-testid="settings-tab-captcha">Captcha</TabsTrigger>
          <TabsTrigger value="rules" data-testid="settings-tab-rules">Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <GeneralTab />
        </TabsContent>
        <TabsContent value="chat" className="mt-4">
          <ChatWidgetTab />
        </TabsContent>
        <TabsContent value="vicibots-widget" className="mt-4">
          <ViciBotsWidgetTab />
        </TabsContent>
        <TabsContent value="videos" className="mt-4">
          <IntroVideos />
        </TabsContent>
        <TabsContent value="demo-calls" className="mt-4">
          <DemoCallsTab />
        </TabsContent>
        <TabsContent value="metadata" className="mt-4">
          <MetaDataTab />
        </TabsContent>
        <TabsContent value="smtp" className="mt-4">
          <SmtpTab />
        </TabsContent>
        <TabsContent value="ssh" className="mt-4">
          <SshTab />
        </TabsContent>
        <TabsContent value="captcha" className="mt-4">
          <CaptchaTab />
        </TabsContent>
        <TabsContent value="rules" className="mt-4">
          <RulesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RulesTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState("0");

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setDays(String(r.data?.call_recording_retention_days ?? 0));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const n = Number.parseInt(String(days).trim(), 10);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a whole number of days (0 keeps recordings forever).");
      return;
    }
    if (n > 3650) {
      toast.error("Maximum is 3650 days.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { call_recording_retention_days: n });
      setDays(String(data.call_recording_retention_days ?? 0));
      toast.success(
        n > 0
          ? `Call recordings older than ${n} day${n === 1 ? "" : "s"} will be deleted.`
          : "Call recordings will be kept until you set a retention period.",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6 space-y-5 max-w-2xl" data-testid="settings-rules">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ListChecks className="w-5 h-5 text-primary" weight="duotone" />
          <h3 className="font-semibold">Rules</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Platform retention rules. After the number of days you enter, all call recordings are deleted from the system.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="delete-call-recordings-days">Delete Call Recordings (days)</Label>
            <Input
              id="delete-call-recordings-days"
              data-testid="settings-recording-retention-days"
              type="number"
              min="0"
              max="3650"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">
              Recordings older than this many days are removed automatically. Use 0 to keep them forever.
            </p>
          </div>
          <Button onClick={save} disabled={saving} data-testid="settings-rules-save">
            {saving ? "Saving…" : "Save rules"}
          </Button>
        </>
      )}
    </div>
  );
}

function GeneralTab() {
  const [health, setHealth] = useState(null);
  useEffect(() => { api.get("/health").then((r) => setHealth(r.data)).catch(() => {}); }, []);

  const rows = [
    ["Currency", "USD (single currency)"],
    ["Money representation", "Integer minor-units (no floats)"],
    ["Billing rounding", "Per-minute (configurable) — applied at CDR billing time"],
    ["Default platform price", "$10.00 / concurrency slot"],
    ["Auth", "Email + password · JWT (Bearer)"],
    ["Tenant isolation", "tenant_id query-guard on every scoped read/write"],
  ];

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-border bg-card card-shadow p-6" data-testid="settings-defaults">
        <div className="flex items-center gap-2 mb-4">
          <Gear className="w-5 h-5 text-primary" weight="duotone" />
          <h3 className="font-semibold">Platform Defaults</h3>
        </div>
        <dl className="divide-y">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-start justify-between py-3 gap-6">
              <dt className="text-sm text-muted-foreground">{k}</dt>
              <dd className="text-sm font-medium text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="rounded-xl border border-border bg-card card-shadow p-6" data-testid="settings-health">
        <h3 className="font-semibold mb-4">Service Health</h3>
        <div className="flex items-center gap-3 rounded-lg border border-border p-4">
          <CheckCircle className={`w-8 h-8 ${health?.status === "ok" ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`} weight="fill" />
          <div>
            <p className="font-medium capitalize">{health?.status || "checking…"}</p>
            <p className="text-sm text-muted-foreground">Database: {health?.db || "—"} · {health?.service}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Telephony stack (OpenSIPS / FreeSWITCH / AI engines) is managed from Servers and AI Engine menus.
        </p>
      </div>
    </div>
  );
}

function ViciBotsWidgetTab() {
  const [enabled, setEnabled] = useState(false);
  const [position, setPosition] = useState("right");
  const [prefix, setPrefix] = useState("");
  const [widgetText, setWidgetText] = useState("");
  const [callLimit, setCallLimit] = useState("0");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [hideOnReseller, setHideOnReseller] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setEnabled(!!r.data?.vicibots_widget_enabled);
        setPosition(r.data?.vicibots_widget_position === "left" ? "left" : "right");
        setPrefix(r.data?.vicibots_widget_campaign_prefix || "");
        setWidgetText(r.data?.vicibots_widget_text || "");
        setCallLimit(String(r.data?.vicibots_widget_call_limit ?? 0));
        setNotifyEmail(r.data?.vicibots_widget_notify_email || "");
        setHideOnReseller(r.data?.vicibots_widget_hide_on_reseller !== false);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (enabled && !prefix.trim()) {
      toast.error("Campaign routing prefix is required when the widget is enabled.");
      return;
    }
    const lim = Number.parseInt(String(callLimit).trim(), 10);
    if (!Number.isFinite(lim) || lim < 0) {
      toast.error("Call limit must be 0 (unlimited) or a positive whole number.");
      return;
    }
    if (notifyEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail.trim())) {
      toast.error("Notify email looks invalid.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        vicibots_widget_enabled: enabled,
        vicibots_widget_position: position,
        vicibots_widget_campaign_prefix: prefix.trim(),
        vicibots_widget_text: widgetText.trim(),
        vicibots_widget_call_limit: lim,
        vicibots_widget_notify_email: notifyEmail.trim(),
        vicibots_widget_hide_on_reseller: hideOnReseller,
      });
      setEnabled(!!data.vicibots_widget_enabled);
      setPosition(data.vicibots_widget_position === "left" ? "left" : "right");
      setPrefix(data.vicibots_widget_campaign_prefix || "");
      setWidgetText(data.vicibots_widget_text || "");
      setCallLimit(String(data.vicibots_widget_call_limit ?? 0));
      setNotifyEmail(data.vicibots_widget_notify_email || "");
      setHideOnReseller(data.vicibots_widget_hide_on_reseller !== false);
      toast.success(
        data.vicibots_widget_enabled
          ? "ViciBots widget enabled on the landing page."
          : "ViciBots widget settings saved.",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6 space-y-5 max-w-2xl" data-testid="settings-vicibots-widget">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Phone className="w-5 h-5 text-primary" weight="duotone" />
          <h3 className="font-semibold">ViciBots Widget</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Web-phone button in the marketing header (next to theme). Visitors click it to start a live
          WebRTC call routed by campaign prefix — same Live engine as Test Call.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div>
              <p className="font-medium text-sm">Enable widget</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {enabled
                  ? "Shown on marketing pages at the selected corner."
                  : "Hidden on the landing page."}
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="vicibots-widget-enabled"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="vicibots-widget-prefix">Campaign routing prefix</Label>
            <Input
              id="vicibots-widget-prefix"
              data-testid="vicibots-widget-prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="e.g. 1001"
              className="font-mono text-sm"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Must match an <span className="font-medium">active</span> campaign&apos;s routing prefix
              (Campaigns → prefix chip). AI talk time bills to that campaign&apos;s customer wallet.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="vicibots-widget-text">Widget text</Label>
            <Input
              id="vicibots-widget-text"
              data-testid="vicibots-widget-text"
              value={widgetText}
              onChange={(e) => setWidgetText(e.target.value.slice(0, 60))}
              placeholder="e.g. Talk to AI"
              maxLength={60}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Optional label next to the theme control in the marketing header.
              With text: phone icon on the left + label. Leave empty for a round phone-only button.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="vicibots-widget-call-limit">Call limit</Label>
            <Input
              id="vicibots-widget-call-limit"
              data-testid="vicibots-widget-call-limit"
              type="number"
              min={0}
              step={1}
              value={callLimit}
              onChange={(e) => setCallLimit(e.target.value)}
              placeholder="0 = unlimited"
              className="font-mono text-sm max-w-[12rem]"
            />
            <p className="text-xs text-muted-foreground">
              Max calls per visitor browser. After this many completed calls the widget is hidden.
              Use <span className="font-medium">0</span> for unlimited.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="vicibots-widget-email">Email (chat transcript)</Label>
            <Input
              id="vicibots-widget-email"
              data-testid="vicibots-widget-email"
              type="email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="ops@example.com"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              On hangup, email is sent only when the call flow collected at least one
              Collect Customer Info field. Includes IP, country, collected fields, and chat
              transcript via platform SMTP (System Settings → SMTP). Leave empty to skip email.
            </p>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-border p-4">
            <input
              id="vicibots-widget-hide-reseller"
              data-testid="vicibots-widget-hide-reseller"
              type="checkbox"
              checked={hideOnReseller}
              onChange={(e) => setHideOnReseller(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-input accent-primary"
            />
            <div>
              <Label htmlFor="vicibots-widget-hide-reseller" className="font-medium text-sm cursor-pointer">
                Hide widget on reseller URL
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When checked, the floating widget does not appear on reseller white-label domains —
                only on the main platform landing pages.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="vicibots-widget-save" className="rounded-lg">
              {saving ? "Saving…" : "Save widget"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ChatWidgetTab() {
  const [chatScript, setChatScript] = useState("");
  const [loadingChat, setLoadingChat] = useState(true);
  const [savingChat, setSavingChat] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setChatScript(r.data?.chat_widget_script || ""))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoadingChat(false));
  }, []);

  const saveChat = async () => {
    setSavingChat(true);
    try {
      const { data } = await api.put("/settings", { chat_widget_script: chatScript });
      setChatScript(data?.chat_widget_script || "");
      toast.success("Chat widget saved — it now shows on the landing page.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingChat(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6" data-testid="settings-chat-widget">
      <div className="flex items-center gap-2 mb-1">
        <ChatCircleDots className="w-5 h-5 text-primary" weight="duotone" />
        <h3 className="font-semibold">Landing Page Chat Widget</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Paste a chat widget embed snippet (e.g. from <span className="font-medium">tawk.to</span>, Crisp, Intercom). It is
        injected <span className="font-medium">only on the public landing page</span> — never inside the app. Leave empty to remove the widget.
      </p>
      <Textarea
        data-testid="chat-widget-script"
        value={chatScript}
        onChange={(e) => setChatScript(e.target.value)}
        disabled={loadingChat || savingChat}
        placeholder={loadingChat ? "Loading…" : '<!--Start of Tawk.to Script-->\n<script type="text/javascript">\n  var Tawk_API=Tawk_API||{};\n  (function(){ var s1=document.createElement("script"); s1.async=true; s1.src="https://embed.tawk.to/XXXXXXXX/default"; s1.charset="UTF-8"; s1.setAttribute("crossorigin","*"); document.body.appendChild(s1); })();\n</script>\n<!--End of Tawk.to Script-->'}
        className="font-mono text-xs min-h-[180px]"
      />
      <div className="flex items-center justify-between mt-3 gap-4">
        <p className="text-xs text-muted-foreground">Raw HTML/JS is executed on the landing page. Only paste snippets from providers you trust.</p>
        <Button onClick={saveChat} disabled={loadingChat || savingChat} data-testid="chat-widget-save" className="rounded-lg shrink-0">
          {savingChat ? "Saving…" : "Save widget"}
        </Button>
      </div>
    </div>
  );
}

function MetaDataTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    og_title: "",
    og_description: "",
    twitter_card: "summary_large_image",
    robots: "index,follow",
    canonical_url: "",
  });
  const [ogImage, setOgImage] = useState(null);
  const [ogPreview, setOgPreview] = useState(null);
  const [clearOgImage, setClearOgImage] = useState(false);

  useEffect(() => {
    api.get("/settings/seo")
      .then((r) => {
        const d = r.data || {};
        setForm({
          meta_title: d.meta_title_raw || "",
          meta_description: d.meta_description_raw || d.meta_description || "",
          meta_keywords: d.meta_keywords || "",
          og_title: d.og_title_raw || "",
          og_description: d.og_description_raw || "",
          twitter_card: d.twitter_card || "summary_large_image",
          robots: d.robots || "index,follow",
          canonical_url: d.canonical_url || "",
        });
        setOgPreview(d.og_image_url || null);
        setOgImage(null);
        setClearOgImage(false);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const onOgFile = (file) => {
    setOgImage(file || null);
    setClearOgImage(false);
    if (file) setOgPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = new FormData();
      body.append("meta_title", form.meta_title.trim());
      body.append("meta_description", form.meta_description.trim());
      body.append("meta_keywords", form.meta_keywords.trim());
      body.append("og_title", form.og_title.trim());
      body.append("og_description", form.og_description.trim());
      body.append("twitter_card", form.twitter_card);
      body.append("robots", form.robots.trim() || "index,follow");
      body.append("canonical_url", form.canonical_url.trim());
      if (ogImage) body.append("og_image", ogImage);
      if (clearOgImage) body.append("clear_og_image", "1");
      const { data } = await api.put("/settings/seo", body);
      setForm({
        meta_title: data.meta_title_raw || "",
        meta_description: data.meta_description_raw || data.meta_description || "",
        meta_keywords: data.meta_keywords || "",
        og_title: data.og_title_raw || "",
        og_description: data.og_description_raw || "",
        twitter_card: data.twitter_card || "summary_large_image",
        robots: data.robots || "index,follow",
        canonical_url: data.canonical_url || "",
      });
      setOgPreview(data.og_image_url || null);
      setOgImage(null);
      setClearOgImage(false);
      toast.success("SEO / meta data saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6 space-y-5 max-w-3xl" data-testid="settings-metadata">
      <div className="flex items-center gap-2">
        <Globe className="w-5 h-5 text-primary" weight="duotone" />
        <div>
          <h3 className="font-semibold">SEO &amp; social share meta</h3>
          <p className="text-sm text-muted-foreground">
            Search engine title/description and the preview card shown when your site is shared on WhatsApp, Facebook, LinkedIn, Twitter/X, etc.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="seo-meta-title">Meta title</Label>
              <Input
                id="seo-meta-title"
                data-testid="seo-meta-title"
                value={form.meta_title}
                onChange={set("meta_title")}
                placeholder="ViciBots — AI Voice Agents for VICIdial"
                maxLength={120}
              />
              <p className="text-[11px] text-muted-foreground">Browser tab / Google title. Falls back to Branding web title if empty.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="seo-meta-description">Meta description</Label>
              <Textarea
                id="seo-meta-description"
                data-testid="seo-meta-description"
                value={form.meta_description}
                onChange={set("meta_description")}
                placeholder="AI voice agents that call, qualify, and warm-transfer leads on your SIP infrastructure."
                maxLength={320}
                className="min-h-[88px]"
              />
              <p className="text-[11px] text-muted-foreground">{form.meta_description.length}/320 — aim for ~150–160 characters.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="seo-meta-keywords">Keywords</Label>
              <Input
                id="seo-meta-keywords"
                data-testid="seo-meta-keywords"
                value={form.meta_keywords}
                onChange={set("meta_keywords")}
                placeholder="AI voice agents, VICIdial, outbound dialer, SIP"
                maxLength={400}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seo-robots">Robots</Label>
              <Input
                id="seo-robots"
                data-testid="seo-robots"
                value={form.robots}
                onChange={set("robots")}
                placeholder="index,follow"
                maxLength={80}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seo-canonical">Canonical URL</Label>
              <Input
                id="seo-canonical"
                data-testid="seo-canonical"
                value={form.canonical_url}
                onChange={set("canonical_url")}
                placeholder="https://dev.wacalls.com/"
                maxLength={300}
              />
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <h4 className="font-medium text-sm">Social share (Open Graph / Twitter)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="seo-og-title">Share title</Label>
                <Input
                  id="seo-og-title"
                  data-testid="seo-og-title"
                  value={form.og_title}
                  onChange={set("og_title")}
                  placeholder="Leave empty to use meta title"
                  maxLength={120}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="seo-og-description">Share description</Label>
                <Textarea
                  id="seo-og-description"
                  data-testid="seo-og-description"
                  value={form.og_description}
                  onChange={set("og_description")}
                  placeholder="Leave empty to use meta description"
                  maxLength={320}
                  className="min-h-[72px]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-twitter-card">Twitter card</Label>
                <select
                  id="seo-twitter-card"
                  data-testid="seo-twitter-card"
                  value={form.twitter_card}
                  onChange={set("twitter_card")}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="summary_large_image">Large image</option>
                  <option value="summary">Summary</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Share image</Label>
                <Input
                  data-testid="seo-og-image"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => onOgFile(e.target.files?.[0])}
                />
                <p className="text-[11px] text-muted-foreground">Recommended 1200×630 PNG/JPG (under 5 MB). Shown in WhatsApp, Facebook, LinkedIn previews.</p>
                {ogPreview && !clearOgImage && (
                  <div className="mt-2 flex items-start gap-3">
                    <img
                      src={ogPreview.startsWith("blob:") ? ogPreview : ogPreview}
                      alt="Share preview"
                      className="h-24 w-auto max-w-[220px] rounded-lg border border-border object-cover"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={() => { setOgImage(null); setOgPreview(null); setClearOgImage(true); }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="seo-save">
              {saving ? "Saving…" : "Save meta data"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function SmtpTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [form, setForm] = useState({
    enabled: false,
    host: "",
    port: "587",
    username: "",
    password: "",
    from_email: "",
    from_name: "",
    reply_to: "",
    use_tls: true,
    use_ssl: false,
  });
  const [meta, setMeta] = useState({
    password_configured: false,
    password_hint: "",
  });

  const applyResponse = (d) => {
    setForm((prev) => ({
      ...prev,
      enabled: !!d.enabled,
      host: d.host || "",
      port: String(d.port ?? 587),
      username: d.username || "",
      password: "",
      from_email: d.from_email || "",
      from_name: d.from_name || "",
      reply_to: d.reply_to || "",
      use_tls: d.use_tls !== false,
      use_ssl: !!d.use_ssl,
    }));
    setMeta({
      password_configured: !!d.password_configured,
      password_hint: d.password_hint || "",
    });
  };

  useEffect(() => {
    api.get("/settings/smtp")
      .then((r) => applyResponse(r.data || {}))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const payloadFromForm = (includePassword) => {
    const port = parseInt(form.port, 10);
    const body = {
      enabled: !!form.enabled,
      host: form.host.trim(),
      port: Number.isFinite(port) ? port : 587,
      username: form.username.trim(),
      from_email: form.from_email.trim(),
      from_name: form.from_name.trim(),
      reply_to: form.reply_to.trim(),
      use_tls: !!form.use_tls,
      use_ssl: !!form.use_ssl,
    };
    if (includePassword && form.password.trim()) body.password = form.password;
    return body;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/smtp", payloadFromForm(true));
      applyResponse(data || {});
      toast.success("SMTP settings saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      toast.error("Enter a recipient email for the test message.");
      return;
    }
    setTesting(true);
    try {
      const body = { ...payloadFromForm(true), to: testTo.trim() };
      await api.post("/settings/smtp/test", body);
      toast.success(`Test email sent to ${testTo.trim()}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6 space-y-5 max-w-3xl" data-testid="settings-smtp">
      <div className="flex items-center gap-2">
        <EnvelopeSimple className="w-5 h-5 text-primary" weight="duotone" />
        <div>
          <h3 className="font-semibold">SMTP email</h3>
          <p className="text-sm text-muted-foreground">
            Outbound mail for capacity alerts and system notifications. When enabled, SMTP is used
            instead of the managed email proxy. Leave password blank when saving to keep the current one.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-4 cursor-pointer">
            <div>
              <p className="text-sm font-medium">Enable SMTP</p>
              <p className="text-xs text-muted-foreground">Use these credentials for all platform transactional email.</p>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              data-testid="smtp-enabled"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="smtp-host">SMTP host</Label>
              <Input id="smtp-host" data-testid="smtp-host" value={form.host} onChange={set("host")} placeholder="smtp.example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-port">Port</Label>
              <Input id="smtp-port" data-testid="smtp-port" value={form.port} onChange={set("port")} placeholder="587" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-username">Username</Label>
              <Input id="smtp-username" data-testid="smtp-username" value={form.username} onChange={set("username")} placeholder="apikey or user@domain" autoComplete="off" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="smtp-password">
                Password {meta.password_configured && (
                  <span className="text-muted-foreground font-normal">· saved {meta.password_hint}</span>
                )}
              </Label>
              <Input
                id="smtp-password"
                data-testid="smtp-password"
                type="password"
                value={form.password}
                onChange={set("password")}
                placeholder={meta.password_configured ? "•••••••• (leave blank to keep)" : "SMTP password"}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-from-email">From email</Label>
              <Input id="smtp-from-email" data-testid="smtp-from-email" value={form.from_email} onChange={set("from_email")} placeholder="noreply@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-from-name">From name</Label>
              <Input id="smtp-from-name" data-testid="smtp-from-name" value={form.from_name} onChange={set("from_name")} placeholder="WA Calls Platform" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="smtp-reply-to">Reply-To (optional)</Label>
              <Input id="smtp-reply-to" data-testid="smtp-reply-to" value={form.reply_to} onChange={set("reply_to")} placeholder="support@example.com" />
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={form.use_tls}
                onCheckedChange={(v) => setForm({ ...form, use_tls: v, use_ssl: v ? false : form.use_ssl })}
                data-testid="smtp-use-tls"
              />
              STARTTLS (typical port 587)
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={form.use_ssl}
                onCheckedChange={(v) => setForm({ ...form, use_ssl: v, use_tls: v ? false : form.use_tls })}
                data-testid="smtp-use-ssl"
              />
              SSL/TLS (typical port 465)
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={save} disabled={saving} data-testid="smtp-save" className="rounded-lg">
              {saving ? "Saving…" : "Save SMTP settings"}
            </Button>
          </div>

          <div className="rounded-lg border border-border p-4 space-y-3">
            <h4 className="text-sm font-semibold">Send test email</h4>
            <p className="text-xs text-muted-foreground">
              Uses the form values above (including an unsaved password). Save after a successful test if you have not already.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="smtp-test-to">Recipient</Label>
                <Input
                  id="smtp-test-to"
                  data-testid="smtp-test-to"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <Button
                variant="outline"
                onClick={sendTest}
                disabled={testing || !form.host.trim() || !form.from_email.trim()}
                data-testid="smtp-test-send"
                className="rounded-lg"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {testing ? "Sending…" : "Send test email"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SshTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    opensips_ssh_user: "root",
    opensips_ssh_port: "22",
    opensips_ssh_password: "",
    freeswitch_ssh_user: "root",
    freeswitch_ssh_port: "22",
    freeswitch_ssh_password: "",
    freeswitch_esl_password: "",
  });
  const [meta, setMeta] = useState({
    opensips_ssh_password_configured: false,
    opensips_ssh_password_hint: "",
    freeswitch_ssh_password_configured: false,
    freeswitch_ssh_password_hint: "",
    freeswitch_esl_password_configured: false,
    freeswitch_esl_password_hint: "",
    env_path: "",
  });

  const applyResponse = (d) => {
    setForm((prev) => ({
      ...prev,
      opensips_ssh_user: d.opensips_ssh_user || "root",
      opensips_ssh_port: d.opensips_ssh_port || "22",
      opensips_ssh_password: "",
      freeswitch_ssh_user: d.freeswitch_ssh_user || "root",
      freeswitch_ssh_port: d.freeswitch_ssh_port || "22",
      freeswitch_ssh_password: "",
      freeswitch_esl_password: "",
    }));
    setMeta({
      opensips_ssh_password_configured: !!d.opensips_ssh_password_configured,
      opensips_ssh_password_hint: d.opensips_ssh_password_hint || "",
      freeswitch_ssh_password_configured: !!d.freeswitch_ssh_password_configured,
      freeswitch_ssh_password_hint: d.freeswitch_ssh_password_hint || "",
      freeswitch_esl_password_configured: !!d.freeswitch_esl_password_configured,
      freeswitch_esl_password_hint: d.freeswitch_esl_password_hint || "",
      env_path: d.env_path || "",
    });
  };

  useEffect(() => {
    api.get("/settings/ssh")
      .then((r) => applyResponse(r.data || {}))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        opensips_ssh_user: form.opensips_ssh_user.trim(),
        opensips_ssh_port: form.opensips_ssh_port.trim(),
        freeswitch_ssh_user: form.freeswitch_ssh_user.trim(),
        freeswitch_ssh_port: form.freeswitch_ssh_port.trim(),
      };
      if (form.opensips_ssh_password.trim()) body.opensips_ssh_password = form.opensips_ssh_password;
      if (form.freeswitch_ssh_password.trim()) body.freeswitch_ssh_password = form.freeswitch_ssh_password;
      if (form.freeswitch_esl_password.trim()) body.freeswitch_esl_password = form.freeswitch_esl_password;
      const { data } = await api.put("/settings/ssh", body);
      applyResponse(data || {});
      toast.success("SSH credentials saved to .env (not stored in the database).");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6 space-y-5 max-w-3xl" data-testid="settings-ssh">
      <div className="flex items-center gap-2">
        <Key className="w-5 h-5 text-primary" weight="duotone" />
        <div>
          <h3 className="font-semibold">SSH credentials</h3>
          <p className="text-sm text-muted-foreground">
            Shared passwords used when the control plane syncs OpenSIPS trusted IPs and FreeSWITCH ACL / gateways.
            Saved only to <span className="font-mono text-xs">backend/.env</span> — never to MongoDB. AI engines do not use SSH.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="space-y-4 rounded-lg border border-border p-4">
            <h4 className="text-sm font-semibold">OpenSIPS</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ops-ssh-user">SSH user</Label>
                <Input id="ops-ssh-user" data-testid="ops-ssh-user" value={form.opensips_ssh_user} onChange={set("opensips_ssh_user")} placeholder="root" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ops-ssh-port">SSH port</Label>
                <Input id="ops-ssh-port" data-testid="ops-ssh-port" value={form.opensips_ssh_port} onChange={set("opensips_ssh_port")} placeholder="22" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ops-ssh-password">SSH password</Label>
                <Input
                  id="ops-ssh-password"
                  data-testid="ops-ssh-password"
                  type="password"
                  autoComplete="new-password"
                  value={form.opensips_ssh_password}
                  onChange={set("opensips_ssh_password")}
                  placeholder={meta.opensips_ssh_password_configured ? `Configured ${meta.opensips_ssh_password_hint} — leave blank to keep` : "Enter password"}
                />
                <p className="text-[11px] text-muted-foreground">
                  {meta.opensips_ssh_password_configured
                    ? `Currently set (${meta.opensips_ssh_password_hint}). Leave blank to keep the existing password.`
                    : "Not configured yet."}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-lg border border-border p-4">
            <h4 className="text-sm font-semibold">FreeSWITCH</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fs-ssh-user">SSH user</Label>
                <Input id="fs-ssh-user" data-testid="fs-ssh-user" value={form.freeswitch_ssh_user} onChange={set("freeswitch_ssh_user")} placeholder="root" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fs-ssh-port">SSH port</Label>
                <Input id="fs-ssh-port" data-testid="fs-ssh-port" value={form.freeswitch_ssh_port} onChange={set("freeswitch_ssh_port")} placeholder="22" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="fs-ssh-password">SSH password</Label>
                <Input
                  id="fs-ssh-password"
                  data-testid="fs-ssh-password"
                  type="password"
                  autoComplete="new-password"
                  value={form.freeswitch_ssh_password}
                  onChange={set("freeswitch_ssh_password")}
                  placeholder={meta.freeswitch_ssh_password_configured ? `Configured ${meta.freeswitch_ssh_password_hint} — leave blank to keep` : "Enter password"}
                />
                <p className="text-[11px] text-muted-foreground">
                  {meta.freeswitch_ssh_password_configured
                    ? `Currently set (${meta.freeswitch_ssh_password_hint}). Leave blank to keep the existing password.`
                    : "Not configured yet."}
                </p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="fs-esl-password">ESL password</Label>
                <Input
                  id="fs-esl-password"
                  data-testid="fs-esl-password"
                  type="password"
                  autoComplete="new-password"
                  value={form.freeswitch_esl_password}
                  onChange={set("freeswitch_esl_password")}
                  placeholder={meta.freeswitch_esl_password_configured ? `Configured ${meta.freeswitch_esl_password_hint} — leave blank to keep` : "Enter ESL password"}
                />
                <p className="text-[11px] text-muted-foreground">
                  Used for remote <span className="font-mono">fs_cli</span> after ACL / gateway sync.
                  {meta.freeswitch_esl_password_configured
                    ? ` Currently set (${meta.freeswitch_esl_password_hint}).`
                    : " Not configured yet."}
                </p>
              </div>
            </div>
          </div>

          {meta.env_path ? (
            <p className="text-[11px] text-muted-foreground font-mono">Writes to {meta.env_path}</p>
          ) : null}

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="ssh-save">
              {saving ? "Saving…" : "Save SSH settings"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function CaptchaTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState("math");
  const [siteKey, setSiteKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [secretHint, setSecretHint] = useState("");
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [ready, setReady] = useState(false);
  const [providers, setProviders] = useState([
    { id: "math", label: "Math challenge", needs_keys: false },
    { id: "recaptcha_v2", label: "Google reCAPTCHA v2", needs_keys: true },
    { id: "turnstile", label: "Cloudflare Turnstile", needs_keys: true },
  ]);

  const needsKeys = provider === "recaptcha_v2" || provider === "turnstile";

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setEnabled(!!r.data?.captcha_enabled);
        setProvider(r.data?.captcha_provider || "math");
        setSiteKey(r.data?.captcha_site_key || "");
        setSecretHint(r.data?.captcha_secret_hint || "");
        setSecretConfigured(!!r.data?.captcha_secret_configured);
        setReady(!!r.data?.captcha_ready);
        if (Array.isArray(r.data?.captcha_providers) && r.data.captcha_providers.length) {
          setProviders(r.data.captcha_providers);
        }
        setSecretKey("");
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (enabled && needsKeys && !siteKey.trim()) {
      toast.error("Site key is required for this captcha provider.");
      return;
    }
    if (enabled && needsKeys && !secretConfigured && !secretKey.trim()) {
      toast.error("Secret key is required when enabling this provider for the first time.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        captcha_enabled: enabled,
        captcha_provider: provider,
        captcha_site_key: siteKey.trim(),
      };
      if (secretKey.trim()) body.captcha_secret_key = secretKey.trim();
      const { data } = await api.put("/settings", body);
      setEnabled(!!data.captcha_enabled);
      setProvider(data.captcha_provider || "math");
      setSiteKey(data.captcha_site_key || "");
      setSecretHint(data.captcha_secret_hint || "");
      setSecretConfigured(!!data.captcha_secret_configured);
      setReady(!!data.captcha_ready);
      setSecretKey("");
      if (data.captcha_enabled && !data.captcha_ready) {
        toast.message("Captcha enabled flag saved, but it will not enforce until keys are set.");
      } else {
        toast.success(data.captcha_ready ? "Captcha enabled on login and signup." : "Captcha settings saved.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const providerHelp = {
    math: "Built-in arithmetic challenge (e.g. 4 + 7). No third-party account or keys required — best default.",
    recaptcha_v2: (
      <>
        Create keys at{" "}
        <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noreferrer" className="text-primary underline">
          Google reCAPTCHA Admin
        </a>{" "}
        (v2 → “I’m not a robot” Checkbox). Add your domains.
      </>
    ),
    turnstile: (
      <>
        Privacy-friendly alternative. Create a widget at{" "}
        <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noreferrer" className="text-primary underline">
          Cloudflare Turnstile
        </a>{" "}
        and paste the site + secret keys.
      </>
    ),
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6 space-y-5 max-w-2xl" data-testid="settings-captcha">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-primary" weight="duotone" />
          <h3 className="font-semibold">Login &amp; signup captcha</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose a provider to protect login and signup against bots. Math is zero-config;
          Google reCAPTCHA and Cloudflare Turnstile need API keys.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div>
              <p className="font-medium text-sm">Enable captcha</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {ready
                  ? "Active on login and signup pages."
                  : enabled
                    ? needsKeys
                      ? "Waiting for site key + secret key."
                      : "Almost ready…"
                    : "Disabled — forms work without captcha."}
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="captcha-enabled"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="captcha-provider">Provider</Label>
            <select
              id="captcha-provider"
              data-testid="captcha-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{providerHelp[provider]}</p>
          </div>

          {needsKeys && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="captcha-site-key">Site key (public)</Label>
                <Input
                  id="captcha-site-key"
                  data-testid="captcha-site-key"
                  value={siteKey}
                  onChange={(e) => setSiteKey(e.target.value)}
                  placeholder={provider === "turnstile" ? "0x4AAAA…" : "6Lc…"}
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="captcha-secret-key">Secret key</Label>
                <Input
                  id="captcha-secret-key"
                  data-testid="captcha-secret-key"
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={secretConfigured ? `Leave blank to keep ${secretHint || "existing secret"}` : "Secret key…"}
                  autoComplete="new-password"
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Never shared to the browser. Leave blank when saving to keep the current secret.
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="captcha-save" className="rounded-lg">
              {saving ? "Saving…" : "Save captcha"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function DemoCallsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newDetails, setNewDetails] = useState("");
  const [newAudio, setNewAudio] = useState(null);
  const [newHome, setNewHome] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = () =>
    api.get("/settings/demo-calls")
      .then((r) => setList(r.data?.demo_calls || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("title", newTitle.trim());
      fd.append("details", newDetails.trim());
      if (newHome) fd.append("home_page", "true");
      if (newAudio) fd.append("audio", newAudio);
      await api.post("/settings/demo-calls", fd);
      setNewTitle("");
      setNewDetails("");
      setNewAudio(null);
      setNewHome(false);
      toast.success("Demo call added.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6" data-testid="settings-demo-calls">
      <div className="flex items-center gap-2 mb-1">
        <Headphones className="w-5 h-5 text-primary" />
        <h3 className="font-semibold">AI Demo Calls</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Upload demo call recordings with a title and details. Details appear under the title on the
        landing page. Mark one as &quot;Set for home page&quot; to play from the landing hero. Audio max 50MB
        (mp3, wav, m4a, aac, ogg, webm, flac).
      </p>

      <div className="rounded-lg border border-border bg-muted/30 p-4 mb-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="demo-new-title">Title</Label>
            <Input
              id="demo-new-title"
              data-testid="demo-call-new-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="e.g. Outbound sales demo"
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="demo-new-details">Details</Label>
            <Input
              id="demo-new-details"
              data-testid="demo-call-new-details"
              value={newDetails}
              onChange={(e) => setNewDetails(e.target.value)}
              placeholder="e.g. Featured on home page"
              maxLength={240}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="demo-new-audio">Audio recording</Label>
          <Input
            id="demo-new-audio"
            data-testid="demo-call-new-audio"
            type="file"
            accept="audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/webm,audio/flac,.mp3,.wav,.m4a,.aac,.ogg,.webm,.flac"
            onChange={(e) => setNewAudio(e.target.files?.[0] || null)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <Switch
              checked={newHome}
              onCheckedChange={setNewHome}
              data-testid="demo-call-new-home"
            />
            Set for home page
          </label>
          <Button onClick={add} disabled={adding || !newTitle.trim()} data-testid="demo-call-add" className="rounded-lg">
            <Plus className="w-4 h-4 mr-1" /> {adding ? "Adding…" : "Add recording"}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="demo-call-empty">No demo calls yet. Add one above.</p>
      ) : (
        <div className="space-y-4">
          {list.map((it) => <DemoCallRow key={it.id} item={it} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}

function DemoCallRow({ item, onChanged }) {
  const [title, setTitle] = useState(item.title || "");
  const [details, setDetails] = useState(item.details || "");
  const [audioFile, setAudioFile] = useState(null);
  const [homePage, setHomePage] = useState(!!item.home_page);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTitle(item.title || "");
    setDetails(item.details || "");
    setHomePage(!!item.home_page);
  }, [item.id, item.title, item.details, item.home_page, item.updated_at]);

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("details", details);
      fd.append("home_page", homePage ? "true" : "false");
      if (audioFile) fd.append("audio", audioFile);
      await api.put(`/settings/demo-calls/${item.id}`, fd);
      toast.success("Saved.");
      setAudioFile(null);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${item.title}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/settings/demo-calls/${item.id}`);
      toast.success("Removed.");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid={`demo-call-row-${item.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {item.home_page && (
          <span className="text-xs font-medium rounded-full bg-primary/10 text-primary px-2.5 py-1" data-testid={`demo-call-home-badge-${item.id}`}>
            Home page
          </span>
        )}
        {item.has_audio && (
          <span className="text-xs text-[hsl(var(--success))] inline-flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {item.audio_filename || "audio uploaded"}
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            maxLength={120}
            data-testid={`demo-call-title-${item.id}`}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Details</Label>
          <Input
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Shown under the title on the landing page"
            maxLength={240}
            data-testid={`demo-call-details-${item.id}`}
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 items-end">
        <label className="text-xs text-muted-foreground">
          <span className="flex items-center gap-1 mb-1">
            <Upload className="w-3.5 h-3.5" /> Audio recording
          </span>
          <input
            type="file"
            accept="audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/webm,audio/flac,.mp3,.wav,.m4a,.aac,.ogg,.webm,.flac"
            onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
            data-testid={`demo-call-file-${item.id}`}
            className="block w-full text-xs"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer pb-1">
          <Switch
            checked={homePage}
            onCheckedChange={setHomePage}
            data-testid={`demo-call-home-${item.id}`}
          />
          Set for home page
        </label>
      </div>
      {item.has_audio && (
        <audio
          controls
          preload="none"
          src={`${API}/settings/demo-calls/${item.id}/audio?v=${encodeURIComponent(item.updated_at || "")}`}
          className="w-full max-w-md h-9"
          data-testid={`demo-call-preview-${item.id}`}
        />
      )}
      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={busy} data-testid={`demo-call-save-${item.id}`} className="rounded-lg">
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button onClick={remove} disabled={busy} variant="outline" data-testid={`demo-call-remove-${item.id}`} className="rounded-lg text-destructive">
          <Trash2 className="w-4 h-4 mr-1" /> Remove
        </Button>
      </div>
    </div>
  );
}

function IntroVideos() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () =>
    api.get("/settings/intro-videos")
      .then((r) => setList(r.data?.intro_videos || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("title", newTitle.trim());
      await api.post("/settings/intro-videos", fd);
      setNewTitle("");
      toast.success("Video added.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card card-shadow p-6" data-testid="settings-intro-videos">
      <div className="flex items-center gap-2 mb-1"><Video className="w-5 h-5 text-primary" /><h3 className="font-semibold">Landing Page Intro Videos</h3></div>
      <p className="text-sm text-muted-foreground mb-4">
        Upload intro videos and their cover images. Covers appear on the public landing page;
        visitors click play to watch the video in a popup. Video max 150MB (mp4/mov/webm), cover max 10MB (png/jpg/webp).
      </p>

      <div className="flex items-center gap-2 mb-5">
        <Input
          data-testid="intro-video-new-title"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="New video title…"
          className="max-w-sm"
        />
        <Button onClick={add} disabled={adding || !newTitle.trim()} data-testid="intro-video-add" className="rounded-lg">
          <Plus className="w-4 h-4 mr-1" /> {adding ? "Adding…" : "Add video"}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="intro-video-empty">No intro videos yet. Add one above.</p>
      ) : (
        <div className="space-y-4">
          {list.map((it) => <IntroVideoRow key={it.id} item={it} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}

function IntroVideoRow({ item, onChanged }) {
  const [title, setTitle] = useState(item.title || "");
  const [videoFile, setVideoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const coverUrl = item.has_cover
    ? `${API}/settings/intro-videos/${item.id}/cover?v=${encodeURIComponent(item.updated_at || "")}`
    : null;

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      if (videoFile) fd.append("video", videoFile);
      if (coverFile) fd.append("cover", coverFile);
      await api.put(`/settings/intro-videos/${item.id}`, fd);
      toast.success("Saved.");
      setVideoFile(null); setCoverFile(null);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${item.title}"?`)) return;
    setBusy(true);
    try { await api.delete(`/settings/intro-videos/${item.id}`); toast.success("Removed."); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col md:flex-row gap-4" data-testid={`intro-video-row-${item.id}`}>
      <div className="w-full md:w-40 shrink-0">
        <div className="aspect-video rounded-md overflow-hidden bg-muted grid place-items-center">
          {coverUrl
            ? <img src={coverUrl} alt={item.title} className="w-full h-full object-cover" data-testid={`intro-video-cover-${item.id}`} />
            : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
        </div>
      </div>
      <div className="flex-1 space-y-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid={`intro-video-title-${item.id}`} />
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-muted-foreground">
            <span className="flex items-center gap-1 mb-1"><Upload className="w-3.5 h-3.5" /> Video {item.has_video && <span className="text-[hsl(var(--success))] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> uploaded</span>}</span>
            <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              data-testid={`intro-video-file-${item.id}`} className="block w-full text-xs" />
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="flex items-center gap-1 mb-1"><ImageIcon className="w-3.5 h-3.5" /> Cover image {item.has_cover && <span className="text-[hsl(var(--success))] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> uploaded</span>}</span>
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif"
              onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
              data-testid={`intro-cover-file-${item.id}`} className="block w-full text-xs" />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={busy} data-testid={`intro-video-save-${item.id}`} className="rounded-lg">
            {busy ? "Saving…" : "Save"}
          </Button>
          <Button onClick={remove} disabled={busy} variant="outline" data-testid={`intro-video-remove-${item.id}`} className="rounded-lg text-destructive">
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
      </div>
    </div>
  );
}
